import { createSelector } from '@reduxjs/toolkit'
import { MagentoCategoryItem, Category, CategoryList } from 'lib/types'
import { CATEGORY, SLASH } from 'lib/core/resources/constants'
import { RootState } from 'lib/core/data/reducer'
import { getSlice as getStoreConfig } from 'lib/core/data/common/storeConfig/selectors'
import orderBy from 'lodash/orderBy'

const MEDIA_URL = '/media/catalog/category'
const CATEGORY_IMAGE_URL = `${process.env.REACT_APP_MAGENTO_URI}${MEDIA_URL}`

interface MagentoCategoryItemWithAttr extends MagentoCategoryItem {
  attr: Record<string, string>
}

export const normalizeCategory = (
  category: MagentoCategoryItemWithAttr,
  categories: Record<string, MagentoCategoryItemWithAttr>,
  categoryUrlSuffix: string
): Category => {
  const parentCategoryId = category.parent_id
  const magentoParentCategory = parentCategoryId && categories[parentCategoryId] ? categories[parentCategoryId] : undefined

  return {
    id: category.id,
    name: category.name,
    image: category.attr[CATEGORY.IMAGE],
    imageText: category.attr[CATEGORY.IMAGE_TEXT],
    children: category.children,
    description: category.attr[CATEGORY.DESCRIPTION],
    isFirstLevel: category.level === CATEGORY.FIRST_LEVEL,
    isActive: category.is_active,
    includeInMenu: category.include_in_menu,
    thumbnail: category.attr[CATEGORY.THUMBNAIL],
    urlPath: `${category.attr[CATEGORY.URL_PATH]}${categoryUrlSuffix}`,
    level: category.level,
    position: category.position,
    customAttributes: category.custom_attributes,
    attr: category.attr,
    layout: category.pwa_layout ?? '',
    parentCategory: magentoParentCategory
      ? normalizeCategory(magentoParentCategory, categories, categoryUrlSuffix)
      : undefined
  }
}

export const getPayload = (state: RootState): MagentoCategoryItem[] => {
  return state.common.categoryList.payload
}

export const getIsFetching = (state: RootState): boolean => {
  return state.common.categoryList.isFetching
}

export const getCategoryList = createSelector(
  getPayload,
  getStoreConfig,
  (items, storeConfig): CategoryList => {
    const urlPathIndex: Record<string, Category> = {}
    const parentIdIndex: Record<string, Category[]> = {}
    const idIndex: Record<string, Category> = {}
    const categoryUrlSuffix = storeConfig.payload?.category_url_suffix ?? ''

    const categoryById: Record<string, MagentoCategoryItemWithAttr> = {}
    const categoriesWithAttr: MagentoCategoryItemWithAttr[] = items.map(item => {
      const element: MagentoCategoryItemWithAttr = {
        ...item,
        attr: {}
      }

      const { id, custom_attributes: customAttributes } = element

      if (customAttributes) {
        const attributes: Record<string, any> = {}
        customAttributes.forEach(attr => {
          const {
            attribute_code: attributeCode,
            value
          } = attr

          switch (attributeCode) {
            case CATEGORY.IMAGE: {
              attributes[attributeCode] = getCategoryImageUrl(value)
              break
            }

            case CATEGORY.THUMBNAIL: {
              attributes[attributeCode] = getCategoryImageUrl(value)
              break
            }

            default:
              attributes[attributeCode] = value
              break
          }
        })

        element.attr = {
          ...element.attr,
          ...attributes
        }
      }

      categoryById[id] = element

      return element
    })

    const list = categoriesWithAttr.map((element) => {
      const category = normalizeCategory(element, categoryById, categoryUrlSuffix)
      const parentId = category.parentCategory?.id
      const urlPath = category.urlPath

      if (urlPath) {
        urlPathIndex[urlPath] = category
      }

      if (parentId) {
        if (!parentIdIndex[parentId]) {
          parentIdIndex[parentId] = []
        }
        parentIdIndex[parentId].push(category)
      }

      idIndex[category.id] = category

      return category
    })

    return {
      list: orderBy(list, [CATEGORY.POSITION, CATEGORY.NAME], ['asc', 'asc']),
      urlPath: urlPathIndex,
      parentId: parentIdIndex,
      id: idIndex
    }
  }
)

const getCategoryImageUrl = (value: string) => {
  if (value.startsWith('http')) {
    return value
  } else if (value.startsWith(MEDIA_URL)) {
    return `${process.env.REACT_APP_MAGENTO_URI}${value}`
  } else {
    return CATEGORY_IMAGE_URL + SLASH + value
  }
}
